import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Image } from '$partials/media';
import AnimatedNumberCounter from './animated-number-counter';
import { CarouselProvider } from '$contexts/carousel';
import Carousel, { CarouselSlide } from '$partials/ui/carousel';
/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */

// Module Big Numbers
export const ModuleStatsItem = ({
    className,
    eyebrowTitle,
    statistic,
    body,
    icon,
}) => {
    const [progress, setProgress] = useState(-1);
    const rx = /(?<number>[\d|.,]+)|(?<text>[\D]+)/g;
    let matches, output = [];

    while (matches = rx.exec(statistic)) {
        output.push(matches.groups);
    }

    const handleAnimationEnd = (i) => setProgress(i);
    let shouldRenderItem = true;
    return (
        <div className={`flex flex-col justify-between md:p-40 p-30 rounded-button bg-core-sand ${className}`}>
            <div>
                <div className='flex flex-row items-center text-grey-off-black'>
                    {icon && (
                        <div
                            className='mr-10'
                            style={{
                                fontSize: '18px',
                                width: `${(icon?.width / icon?.height)}em` ,
                                maxWidth: '100%'
                            }}>
                            <Image
                                {...icon}
                                loading={''}/>
                        </div>
                    )}
                    {eyebrowTitle && (
                        <p className='type-style-7'>
                            {eyebrowTitle}
                        </p>
                    )}
                </div>
                <div className='flex flex-row mt-25 md:mt-20'>
                    {output.map((item, i) => {
                        if (!shouldRenderItem) { return null; }
                        const decimalPlaces = item.number?.toString?.().split?.(/\.|,/)?.[1]?.length;

                        const result = (
                            item.number ? (
                                <div
                                    className='relative type-style-6 text-grey-black'
                                    key={i}>
                                    <div className='absolute'>
                                        <AnimatedNumberCounter
                                            decimalPlaces={decimalPlaces}
                                            onEnd={handleAnimationEnd.bind(null, i)}
                                            toValue={parseFloat(item.number?.replace(',', '.'))} />
                                    </div>
                                    <span className='opacity-0'>{item.number}</span>
                                </div>
                            ) : (
                                <span
                                    className='type-style-6 text-grey-black'
                                    key={i}>{item.text}</span>
                            )
                        );

                        if (i > progress && item.number && shouldRenderItem) {
                            shouldRenderItem = false;
                        }
                        return result;
                    })}
                </div>

                <p className='type-style-8 text-grey-off-black mt-16'>{body}</p>
            </div>
        </div>
    );
};

const ModuleStats = ({
    caption,
    header,
    itemsCollection,
    parent
}) => {
    const items = itemsCollection.items;
    return (
        <div className={`bg-white relative overflow-hidden ${parent === 'Home' ? 'py-40 md:py-50' : parent === 'Detail' ? 'py-20 md:py-30' : 'py-40 md:py-60'}`}>
            <div className='layout-container'>

                <div className='bg-white mx-10 sm:mx-5 md:mx-10'>
                    {header && (
                        <p className='type-style-3 mb-30'>
                            {header}
                        </p>
                    )}

                    {/* Desktop */}
                    <div className={`${items.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} hidden md:grid grid-cols-1 sm:grid-cols-2 gap-20 sm:gap-10 md:gap-20`}>
                        {items.length === 2 ?
                            items.map((item, i) =>
                                <ModuleStatsItem
                                    key={i}
                                    {...item} />
                            ) :
                            items.map((item, i) =>
                                <ModuleStatsItem
                                    key={i}
                                    {...item} />
                            )
                        }
                    </div>

                    {/* Tablet / Mobile Carousel */}
                    <div className={`overflow-hidden w-screen max-w-screen left-1/2 relative -translate-x-1/2 mt-40 ${items.length > 2 ? '' : 'sm:hidden'} md:hidden`}>
                        <CarouselProvider>
                            <div className='layout-container'>
                                <Carousel className='flex swiper-wrapper h-auto w-full'>
                                    {items.map((item, i) => {
                                        if (!item) {return null;}
                                        return (
                                            <CarouselSlide
                                                className='h-auto flex px-10 sm:px-5 w-11/12 sm:w-1/2'
                                                index={i}
                                                key={`stats-carousel-${i}`}>
                                                <ModuleStatsItem
                                                    className='w-full'
                                                    key={i}
                                                    {...item} />
                                            </CarouselSlide>
                                        );})}
                                </Carousel>
                            </div>
                        </CarouselProvider>
                    </div>
                </div>

                <div className={`hidden ${items.length > 2 ? '' : 'sm:grid'} md:hidden grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-20 sm:gap-10 px-10 sm:px-5`}>
                    {items.length === 2 ?
                        items.map((item, i) =>
                            <ModuleStatsItem
                                className={i === 0 ? 'md:col-start-2' : ''}
                                key={i}
                                {...item} />
                        ) :
                        items.map((item, i) =>
                            <ModuleStatsItem
                                key={i}
                                {...item} />
                        )
                    }
                </div>

                {caption && (
                    <p className='type-style-10 text-dark-grey mt-25 px-10 sm:px-5 md:px-10 md:w-6/12'>{caption}</p>
                )}
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

ModuleStatsItem.propTypes = {
    icon: PropTypes.shape({
        src: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired
    }),
    eyebrowTitle: PropTypes.string,
    statistic: PropTypes.string,
    body: PropTypes.string,
    className: PropTypes.string
};

ModuleStats.propTypes = {
    caption: PropTypes.string,
    header: PropTypes.string,
    parent: PropTypes.string,
    itemsCollection: PropTypes.object
};

export default ModuleStats;
